/**
 * Which way the design tilt is measured, and the plane the modules end up on.
 *
 * A copy of pv_core/mounting.py for the browser -- the 3D view, the layout
 * sheet request and the orientation captions need the answer without a round
 * trip -- pinned to a fixture that module generates (mountingFixture.json), so
 * the two cannot drift apart unnoticed.
 *
 * "Plane tilt" in pvlib and PVsyst is from the HORIZONTAL. With the reference
 * set to "ground" the racking follows the terrain, so the typed tilt is between
 * the modules and the rails and the modelled plane is that angle composed with
 * the site's fitted ground plane (tilt and downhill azimuth).
 */

const DEG = Math.PI / 180;

export const TILT_REFERENCES = ["horizontal", "ground"];

export function normalizeTiltReference(raw) {
  const text = String(raw || "").trim().toLowerCase();
  return TILT_REFERENCES.includes(text) ? text : "horizontal";
}

const num = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

function normal(tiltDeg, azimuthDeg) {
  const t = tiltDeg * DEG, a = azimuthDeg * DEG;
  return [Math.sin(t) * Math.sin(a), Math.sin(t) * Math.cos(a), Math.cos(t)];
}

function rotate(v, k, angle) {
  const c = Math.cos(angle), s = Math.sin(angle);
  const dot = k[0] * v[0] + k[1] * v[1] + k[2] * v[2];
  const cross = [k[1] * v[2] - k[2] * v[1], k[2] * v[0] - k[0] * v[2], k[0] * v[1] - k[1] * v[0]];
  return [0, 1, 2].map((i) => v[i] * c + cross[i] * s + k[i] * dot * (1 - c));
}

/**
 * @returns {{tilt:number, azimuth:number, designTilt:number, designAzimuth:number,
 *            reference:string, slopeTilt:number, slopeAzimuth:number|null, changed:boolean}}
 */
export function effectiveOrientation(tiltDeg, azimuthDeg, { slopeTilt, slopeAzimuth, reference } = {}) {
  const ref = normalizeTiltReference(reference);
  const tilt = num(tiltDeg) ?? 0;
  const azimuth = num(azimuthDeg) ?? 180;
  const slope = num(slopeTilt) ?? 0;
  const downhill = num(slopeAzimuth);
  const base = { designTilt: tilt, designAzimuth: azimuth, reference: ref, slopeTilt: slope, slopeAzimuth: downhill };
  if (ref !== "ground" || slope <= 0 || downhill === null) {
    return { ...base, tilt, azimuth, changed: false };
  }
  const a = downhill * DEG;
  const axis = [-Math.cos(a), Math.sin(a), 0];
  const n = rotate(normal(tilt, azimuth), axis, slope * DEG);
  const nz = Math.max(-1, Math.min(1, n[2]));
  const effTilt = Math.acos(nz) / DEG;
  const effAz = effTilt > 1e-9 ? ((Math.atan2(n[0], n[1]) / DEG) % 360 + 360) % 360 : azimuth;
  const round4 = (x) => Math.round(x * 1e4) / 1e4;
  const t4 = round4(effTilt), z4 = round4(effAz);
  const dAz = ((z4 - azimuth + 180) % 360 + 360) % 360 - 180;
  return { ...base, tilt: t4, azimuth: z4, changed: Math.abs(t4 - tilt) > 1e-6 || Math.abs(dAz) > 1e-6 };
}

/** The effective plane for a project config, or the typed one when nothing applies. */
export function mountingPlaneFor(cfg, spacingInfo = cfg?.pv_design?.spacingInfo) {
  const po = cfg?.panelOrientation || {};
  const loc = cfg?.loc_info || {};
  const fixed = String(cfg?.mountType || "fixed").toLowerCase() === "fixed";
  const flushRoof = String(po.installationType || "").toLowerCase() === "roof_flush";
  const onRoof = String(po.installationType || "").startsWith("roof");
  const roof = spacingInfo?.site_geometry?.roof;
  return effectiveOrientation(po.tilt, po.azim, {
    // The orientation is the source of truth; stored physical geometry can
    // retain older angles until the layout is opened and saved again.
    slopeTilt: onRoof ? po.roofPitch ?? roof?.pitchDeg ?? 0 : loc.tilt,
    slopeAzimuth: onRoof ? po.roofAzimuth ?? roof?.azimuthDeg ?? 180 : loc.azimuth,
    reference: fixed && !flushRoof ? po.tiltReference : "horizontal",
  });
}

/** One sentence for a caption. */
export function describeMountingPlane(r) {
  if (!r.changed) return `Tilt ${r.tilt.toFixed(1)}° from the horizontal.`;
  return `${r.designTilt.toFixed(1)}° on the racking, which follows the ${r.slopeTilt.toFixed(1)}° ground slope towards ${Math.round(r.slopeAzimuth)}°: the modules stand at ${r.tilt.toFixed(1)}° from the horizontal facing ${r.azimuth.toFixed(1)}°, and that is the plane modelled.`;
}
